// DiagnosticCardExtended.jsx
import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import styles from "../modals/ResourceViewerModal.module.css";
import drstyles from "./DiagnosticReportCardExtended.module.css";
import PDFViewer from "../utils/PDFViewer";
import { useStore } from "react-redux";
import { fetchPatientByReference } from "../services/patientService";
import { fetchProviderByReference } from "../services/providerService";
import { fetchOrganizationByReference } from "../services/organizationService";
import { fetchEncounterByReference } from "../services/encounterService";
import AttachmentHandler from "../components/AttachmentHandler";
import { resolveName, hydrateFhirGraph } from "../utils/messageUtils";
import CardHeader from "./CardHeader";
import { Field, SectionHeader, renderReference, renderCodeable } from "../utils/renderUtils";

/* -------------------- Helpers -------------------- */
const normalizeAttachmentsFromReport = (resource) => {
  const result = [];

  if (Array.isArray(resource.presentedForm)) {
    resource.presentedForm.forEach((att, idx) => {
      result.push({
        title: att.title || `presentedForm ${idx + 1}`,
        url: att.url,
        contentType: att.contentType,
        source: "presentedForm",
        raw: att,
      });
    });
  }

  if (Array.isArray(resource.media)) {
    resource.media.forEach((m, idx) => {
      const comment = m.comment;
      if (m.link?.reference) {
        result.push({
          title: comment || `media ${idx + 1}`,
          url: m.link.reference,
          contentType: null,
          source: "media.link",
          raw: m,
        });
      } else if (m.link?.attachment?.url) {
        result.push({
          title: comment || `media ${idx + 1}`,
          url: m.link.attachment.url,
          contentType: m.link.attachment.contentType,
          source: "media.attachment",
          raw: m,
        });
      }
    });
  }

  return result;
};

/* -------------------- DiagnosticCardExtended -------------------- */
const DiagnosticCardExtended = ({ resource, handleShowNested, fhirClient }) => {
  const store = useStore();
  const [showModal, setShowModal] = useState(false);
  const [modalBlobUrl, setModalBlobUrl] = useState("");
  const [modalTitle, setModalTitle] = useState("");
  const [modalType, setModalType] = useState("generic");
  const attachments = normalizeAttachmentsFromReport(resource);
  const [encounterData, setEncounterData] = useState(null);
  const resultRefs = resource.result || [];
  const [subject, setSubject] = useState(null);
  const [subjectName, setSubjectName] = useState(null);
  const [encounterName, setEncounterName] = useState(null);
  const [performers, setPerformers] = useState([]);
  const [provider, setProvider] = useState(null);
  const [interpreter, setInterpreter] = useState(null);
  const [organization, setOrganization] = useState(null);

  useEffect(() => {
    if (!resource || !fhirClient) return;

    hydrateFhirGraph(resource, fhirClient, store);

    const doFetchResources = async () => {
      // SUBJECT
      if (resource.subject?.reference) {
        const patient = await fetchPatientByReference(resource.subject.reference, fhirClient, store);
        setSubject(patient);
        setSubjectName(resolveName(patient));
      }

      // ENCOUNTER
      if (resource.encounter?.reference) {
        const encounter = await fetchEncounterByReference(resource.encounter.reference, fhirClient, store);
        setEncounterData(encounter);
        setEncounterName(resolveName(encounter));
      }

      // PERFORMERS
      const resolvedPerformers = [];
      const resolvedInterpreters = [];

      let primaryProvider = null;
      let primaryOrganization = null;

      for (const performer of resource.performer ?? []) {
        const ref = performer?.reference;
        if (!ref) continue;

        const [type] = ref.split("/");
        let resolved = null;

        switch (type) {
          case "Practitioner":
            resolved = await fetchProviderByReference(ref, fhirClient, store);
            if (!primaryProvider) {
              primaryProvider = resolved;
              setProvider(resolved);
            }
            break;
          case "Organization":
            resolved = await fetchOrganizationByReference(ref, fhirClient, store);
            if (!primaryOrganization) {
              primaryOrganization = resolved;
              setOrganization(resolved);
            }
            break;
          case "Patient":
            resolved = await fetchPatientByReference(ref, fhirClient, store);
            break;
          default:
            resolved = ref;
        }

        resolvedPerformers.push(resolved || ref);
      }
      setPerformers(resolvedPerformers);

      for (const resultsInterpreter of resource.resultsInterpreter ?? []) {
        const ref = resultsInterpreter?.reference;
        if (!ref) continue;
        const [type] = ref.split("/");
        let resolved = null;

        switch (type) {
          case "Practitioner":
            resolved = await fetchProviderByReference(ref, fhirClient, store);
            if (!primaryProvider) setInterpreter(resolved);
            break;
          default:
            resolved = ref;
        }

        resolvedInterpreters.push(resolved || ref);
      }
      setPerformers(resolvedInterpreters);
    };

    doFetchResources();
  }, [resource, fhirClient, store]);


  return (
    <div className="p-4 border rounded shadow-sm bg-white">
      <CardHeader resource={resource} handleShowNested={handleShowNested} fhirClient={fhirClient} />

      {/* IDENTIFICATION */}
      <SectionHeader>Identification</SectionHeader>
      <div className={styles.indented}>
        <div className={styles.sectionContent}>
          <Field label="Report ID">{resource.id}</Field>
          <Field label="Status">{resource.status}</Field>
          <Field label="Code">{renderCodeable(resource.code)}</Field>
          {Array.isArray(resource.identifier) && resource.identifier.length > 0 && (
            <Field label="Identifiers">
              {resource.identifier.map((id, idx) => (
                <div key={idx} className="ps-3">{id.system} — {id.value}</div>
              ))}
            </Field>
          )}
          {Array.isArray(resource.category) && resource.category.length > 0 && (
            <Field label="Categories">
              {resource.category.map((c, i) => (
                <div key={i} className="mb-2">{renderCodeable(c)}</div>
              ))}
            </Field>
          )}
        </div>
      </div>

      {/* TIMING */}
      <SectionHeader>Timing</SectionHeader>
      <div className={styles.indented}>
        <div className={styles.sectionContent}>
          <Field label="Effective">
            {resource.effectiveDateTime ||
              (resource.effectivePeriod &&
                `${resource.effectivePeriod.start || "?"} → ${resource.effectivePeriod.end || "?"}`) ||
              "N/A"}
          </Field>
          <Field label="Issued">{resource.issued}</Field>
          <div className="mb-1">
            <strong>Encounter:</strong>{" "}
            {resource.encounter?.reference ? renderReference(resource.encounter.reference, resolveName(resource.encounter.reference, store), handleShowNested)
              : <span className="text-muted">None</span>}
          </div>
        </div>
      </div>

      {/* SERVICE & SPECIMEN */}
      <SectionHeader>Service & Specimen</SectionHeader>
      <div className={styles.indented}>
        <div className={styles.sectionContent}>
          <Field label="Performers">
            <div className={drstyles.indentedValues}>
              {resource.performer?.map((p, idx) => (
                <div key={idx}>
                  {renderReference(p.reference || p.display, p.display || p.reference, handleShowNested)}
                </div>
              ))}
            </div>
          </Field>

          <Field label="Results Interpreters">
            <div className={drstyles.indentedValues}>
              {resource.resultsInterpreter?.map((r, idx) => (
                <div key={idx}>
                  {renderReference(r.reference || r.display, r.display || r.reference, handleShowNested)}
                </div>
              ))}
            </div>
          </Field>

          <Field label="Specimens">
            <div className={drstyles.indentedValues}>
              {resource.specimen?.map((r, idx) => (
                <div key={idx}>
                  {renderReference(r.reference || r.display, r.display || r.reference, handleShowNested)}
                </div>
              ))}
            </div>
          </Field>
        </div>
      </div>

      {/* BASED ON */}
      {Array.isArray(resource.basedOn) && resource.basedOn.length > 0 && (
        <>
          <SectionHeader>Based On</SectionHeader>
          <div className={styles.indented}>
            <div className={styles.sectionContent}>
              <div className={drstyles.indentedValues}>
                {resource.basedOn.map((ref, idx) => (
                  <div key={idx}>
                    {renderReference(ref.reference || ref.display, ref.display || ref.reference, handleShowNested)}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* RESULTS */}
      <SectionHeader>Results</SectionHeader>
      <div className={styles.indented}>
        <div className={styles.sectionContent}>
          {resultRefs.length > 0 ? (
            resultRefs.map((r, idx) => (
              <div key={idx} className="mb-2">
                {renderReference(r.reference || r.display, r.display || r.reference, handleShowNested)}
              </div>
            ))
          ) : (
            <div className="text-muted">No result references</div>
          )}
        </div>
      </div>

      {/* IMAGING STUDIES */}
      {Array.isArray(resource.imagingStudy) && resource.imagingStudy.length > 0 && (
        <>
          <SectionHeader>Imaging Studies</SectionHeader>
          <div className={styles.indented}>
            {resource.imagingStudy.map((isr, idx) => (
              <div key={idx} className="mb-2">
                {renderReference(isr.reference || isr.display, isr.display || isr.reference, handleShowNested)}
              </div>
            ))}
          </div>
        </>
      )}

      {/* ATTACHMENTS */}
      <SectionHeader>Attachments</SectionHeader>
      <AttachmentHandler attachments={attachments} fhirClient={fhirClient} />

      {/* CONCLUSION */}
      <SectionHeader>Conclusion</SectionHeader>
      <div className={styles.indented}>
        <div className={styles.sectionContent}>
          <Field label="Conclusion">{resource.conclusion}</Field>
          {Array.isArray(resource.conclusionCode) && resource.conclusionCode.length > 0 && (
            <Field label="Conclusion Codes">
              {resource.conclusionCode.map((cc, i) => (
                <div key={i}>{renderCodeable(cc)}</div>
              ))}
            </Field>
          )}
        </div>
      </div>

      {/* NOTES */}
      {Array.isArray(resource.note) && resource.note.length > 0 && (
        <>
          <SectionHeader>Notes</SectionHeader>
          <div className="ps-5 pt-2">
            {resource.note.map((n, i) => (
              <div key={i} className="mb-2">
                {n.text} <span className="text-muted small">({n.authorString || n.authorReference?.reference})</span>
              </div>
            ))}
          </div>
        </>
      )}

      {/* METADATA */}
      <SectionHeader>Metadata</SectionHeader>
      <div className={styles.indented}>
        <Field label="Language">{resource.language}</Field>
        <Field label="Last Updated">{resource.meta?.lastUpdated}</Field>
        <Field label="Version">{resource.meta?.versionId}</Field>
      </div>

      {/* MODAL PREVIEW */}
      <Modal
        show={showModal}
        onHide={() => { setShowModal(false); URL.revokeObjectURL(modalBlobUrl); setModalBlobUrl(""); }}
      >
        <Modal.Header closeButton>
          <Modal.Title>{modalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ height: "80vh" }}>
          {modalType === "pdf" && <PDFViewer blobUrl={modalBlobUrl} />}
          {modalType === "image" && <img src={modalBlobUrl} alt={modalTitle} style={{ maxWidth: "100%", maxHeight: "100%" }} />}
          {(modalType === "text" || modalType === "json" || modalType === "html") && (
            <iframe src={modalBlobUrl} title={modalTitle} style={{ width: "100%", height: "100%", border: "none" }} />
          )}
          {modalType === "generic" && <p className="text-muted text-center">Preview not supported for this file type.</p>}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default DiagnosticCardExtended;
